import { MasterBus } from "../effects/MasterBus";
import { createMusicState, planBar, resetMusicState } from "../music/MusicEngine";
import type { MusicState } from "../music/musicTypes";
import { createStreams, type RngStreams } from "../utils/random";
import { PendingQueue } from "../scheduling/PendingQueue";
import type { DispatchSink } from "../scheduling/RollingDispatcher";
import { RollingDispatcher, secondsPerTick } from "../scheduling/RollingDispatcher";
import type { NoteEvent, RampEvent } from "../scheduling/ScheduledEvent";
import type { ControlStore } from "../state/controlStore";
import type { ErrorLog } from "../state/errorLog";
import type { EngineSnapshot, EngineStatus, InitStage } from "../state/types";
import { stepTowards } from "../utils/math";
import { AudioParameterController } from "./AudioParameterController";
import {
  BEATS_PER_BAR,
  BPM_MAX_STEP_PER_BAR,
  DISPATCH_WINDOW_SEC,
  MIN_WINDOW_TO_TICK_RATIO,
  PLAN_AHEAD_BARS,
  RESERVATION_SAFETY_SEC,
} from "./constants";
import { DiagnosticClick } from "./DiagnosticClick";
import { DrumKit } from "./DrumKit";
import { DisposeRegistry } from "./DisposeRegistry";
import { EventRegistry } from "./EventRegistry";
import type { ToneAdapter } from "./tone";
import { TransportController } from "./TransportController";

export interface AudioEngineDeps {
  tone: ToneAdapter;
  store: ControlStore;
  errorLog: ErrorLog;
  /** Injected so the resume drain wait is testable without real time. */
  delay?: (ms: number) => Promise<void>;
}

const defaultDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    // Lifecycle timing only — never used to schedule sound.
    setTimeout(resolve, ms);
  });

/**
 * Owns the audio lifecycle. See docs/implementation-plan.md ch.3.
 *
 * Key rules encoded here:
 *  - Stop is pause(): position, music state and the 3 permanent transport
 *    callbacks all survive. Transport.cancel() is never called anywhere.
 *  - The permanent callbacks are registered exactly once in init() and cleared
 *    by id only in dispose().
 *  - While paused or panicked the master output gain stays at 0; resume() is the
 *    only path that raises it, and only after old reservations have drained.
 *  - failed and disposed are terminal: the owner creates a new instance.
 */
export class AudioEngine {
  private readonly tone: ToneAdapter;
  private readonly store: ControlStore;
  private readonly errorLog: ErrorLog;
  private readonly delay: (ms: number) => Promise<void>;

  private readonly eventRegistry = new EventRegistry();
  private readonly disposeRegistry = new DisposeRegistry();
  private readonly queue = new PendingQueue();
  private readonly transportController: TransportController;

  private master: MasterBus | null = null;
  private click: DiagnosticClick | null = null;
  private drums: DrumKit | null = null;
  /** Seeded streams and music state live for the lifetime of the engine. */
  private streams: RngStreams;
  private musicState: MusicState;
  private parameters: AudioParameterController | null = null;
  private dispatcher: RollingDispatcher | null = null;

  private statusValue: EngineStatus = "idle";
  private bar = 0;
  private beat = 0;
  private plannedUpToBar = -1;
  private nextEventId = 1;

  /** AudioContext time at the last pause/reset/panic, for the resume drain wait. */
  private pausedAtSec = 0;
  private resumePending = false;

  private dispatchedEventTotal = 0;
  private lateDropCount = 0;
  private dispatchTickCount = 0;
  private dispatchRateWindowStartMs = 0;
  private dispatchTickHz = 0;
  private windowWarningIssued = false;

  constructor(deps: AudioEngineDeps) {
    this.tone = deps.tone;
    this.store = deps.store;
    this.errorLog = deps.errorLog;
    this.delay = deps.delay ?? defaultDelay;
    this.transportController = new TransportController(this.tone.transport(), this.eventRegistry);

    const controls = this.store.getSnapshot();
    this.streams = createStreams(controls.seed);
    this.musicState = createMusicState(this.streams, {
      genre: controls.genre,
      energy: controls.main.energy,
      density: controls.main.density,
    });
  }

  get status(): EngineStatus {
    return this.statusValue;
  }

  /** Status read that survives an await without being narrowed by the compiler. */
  private readStatus(): EngineStatus {
    return this.statusValue;
  }

  // -------------------------------------------------------------------------
  // init
  // -------------------------------------------------------------------------

  /**
   * Idempotent for repeated clicks. Terminal states throw, because a failed or
   * disposed instance must be replaced rather than reused.
   */
  async init(): Promise<void> {
    if (this.statusValue === "failed" || this.statusValue === "disposed") {
      throw new Error(
        `AudioEngine.init() called on a ${this.statusValue} instance. Create a new instance instead.`,
      );
    }
    if (this.statusValue !== "idle") return;

    this.statusValue = "starting";
    let stage: InitStage = "toneStart";
    try {
      await this.tone.start();

      stage = "contextResume";
      if (this.tone.contextState() !== "running") {
        await this.tone.resumeContext();
      }

      stage = "masterBus";
      this.master = new MasterBus(this.tone, this.disposeRegistry);

      stage = "instruments";
      this.click = new DiagnosticClick(this.tone, this.disposeRegistry);
      this.drums = new DrumKit(this.tone, this.disposeRegistry);
      this.parameters = new AudioParameterController(this.store, this.master);
      this.parameters.start();
      this.dispatcher = new RollingDispatcher(this.queue, this.createSink(), DISPATCH_WINDOW_SEC);

      stage = "musicState";
      // Re-seed here so a seed edited on the start screen takes effect.
      this.streams = createStreams(this.store.getSnapshot().seed);
      resetMusicState(this.musicState, this.streams, this.musicStateInputs());
      this.bar = 0;
      this.beat = 0;
      this.plannedUpToBar = -1;
      const transport = this.tone.transport();
      transport.setBpm(this.store.getSnapshot().transport.bpmManual);
      this.assertWindowInvariant();

      stage = "transportEvents";
      this.transportController.register({
        onDispatchTick: (timeSec) => {
          this.onDispatchTick(timeSec);
        },
        onBarTick: () => {
          this.onBarTick();
        },
        onUiTick: () => {
          this.onUiTick();
        },
      });

      stage = "initialPlan";
      // Plan before starting so there is no silent gap at the beginning.
      this.planFrom(0);

      stage = "transportStart";
      transport.start();
      this.raiseMaster();
      this.statusValue = "running";
    } catch (error) {
      this.rollbackFailedInit(stage, error);
      throw error;
    }
  }

  /**
   * Releases everything acquired so far, in reverse order, then marks the
   * instance terminally failed (docs/implementation-plan.md 3.3.1).
   */
  private rollbackFailedInit(stage: InitStage, error: unknown): void {
    this.errorLog.record(`init:${stage}`, error);
    try {
      this.transportController.clearAll();
      this.tone.transport().pause();
    } catch (cleanupError) {
      this.errorLog.record("init:rollback:transport", cleanupError);
    }
    this.queue.discardAll();
    this.parameters?.stop();
    this.parameters = null;
    this.click = null;
    this.drums = null;
    this.master = null;
    this.dispatcher = null;
    this.disposeRegistry.disposeAll((disposeError) => {
      this.errorLog.record("init:rollback:dispose", disposeError);
    });
    this.statusValue = "failed";
  }

  // -------------------------------------------------------------------------
  // pause / resume / reset / panic
  // -------------------------------------------------------------------------

  /** Stop. A pause that keeps position, state and the permanent callbacks. */
  pause(): void {
    if (this.statusValue !== "running") return;
    this.silenceAndHalt("paused");
    this.statusValue = "paused";
  }

  /**
   * Rebuilds the queue before restarting, otherwise the discarded events would
   * leave up to one bar of silence until the next barTick.
   * Quantised to the next bar boundary (3.5.1).
   */
  async resume(): Promise<void> {
    if (this.statusValue !== "paused" || this.resumePending) return;
    this.resumePending = true;
    try {
      const transport = this.tone.transport();
      this.transportController.assertFull();

      if (this.queue.pendingCount > 0) this.queue.discardAll();

      const resumeBar = this.nextResumeBar();
      transport.setTicks(resumeBar * this.ticksPerBar);
      this.bar = resumeBar;
      this.beat = 0;
      this.plannedUpToBar = resumeBar - 1;
      this.planFrom(resumeBar);

      if (this.store.getSnapshot().diagnosticClick && this.queue.pendingCount === 0) {
        throw new Error("resume() planned no events; the queue rebuild failed");
      }

      const remainingMs = this.reservationDrainRemainingMs();
      if (remainingMs > 0) await this.delay(remainingMs);

      // Panic or dispose may have happened while waiting. Read through a method
      // so the compiler does not narrow the status to its pre-await value.
      if (this.readStatus() !== "paused") return;

      transport.start();
      this.raiseMaster();
      this.statusValue = "running";
    } catch (error) {
      this.errorLog.record("resume", error);
      throw error;
    } finally {
      this.resumePending = false;
    }
  }

  /** Halts and returns transport position, music state and PRNG to the start. */
  reset(): void {
    if (this.statusValue !== "running" && this.statusValue !== "paused") return;
    this.silenceAndHalt("paused");

    const transport = this.tone.transport();
    this.queue.discardAll();
    // stop() is used purely because it resets the tick counter to 0; it does not
    // touch the scheduled-event timelines (verified against Tone 15.1.22).
    transport.stop();
    if (transport.ticks !== 0) {
      this.errorLog.record("reset", new Error(`expected ticks 0, got ${transport.ticks}`));
    }

    this.bar = 0;
    this.beat = 0;
    this.plannedUpToBar = -1;
    // Reset re-applies the seed, so the same session replays identically.
    this.streams = createStreams(this.store.getSnapshot().seed);
    resetMusicState(this.musicState, this.streams, this.musicStateInputs());
    // Master stays at 0: resume() is the only place that raises it.
    this.statusValue = "paused";
  }

  /** Immediate safe stop, available from running, paused and failed. */
  panic(): void {
    if (this.statusValue === "disposed") return;
    this.silenceAndHalt("panicked");
    this.queue.discardAll();
    this.statusValue = "panicked";
  }

  /** Leaves panic. Stays paused and silent; only resume() raises the gain. */
  arm(): void {
    if (this.statusValue !== "panicked") return;
    // The gain is already 0 and must stay 0 — mute() only re-records the reason,
    // so diagnostics stop reporting "panicked" once the user has re-armed.
    this.master?.mute("paused");
    this.statusValue = "paused";
  }

  private silenceAndHalt(reason: "paused" | "panicked"): void {
    this.master?.mute(reason);
    this.pausedAtSec = this.tone.nowSec();
    try {
      this.tone.transport().pause();
    } catch (error) {
      this.errorLog.record("transport:pause", error);
    }
    this.queue.discardUndispatched();
    // Nothing is planned any more: the queue that held those bars was discarded.
    // resume() re-plans from the resume bar, so leaving a stale plannedUpToBar
    // would make plannedBarCount claim bars that no longer have events.
    this.plannedUpToBar = this.bar - 1;
    this.click?.releaseAll();
    this.drums?.releaseAll();
  }

  private raiseMaster(): void {
    const controls = this.store.getSnapshot();
    if (controls.sound.muted) {
      this.master?.mute("muted");
      return;
    }
    this.master?.unmute(controls.sound.masterVolume);
  }

  /**
   * Re-acquires a suspended AudioContext without touching the engine status.
   *
   * Used for both `visibilitychange` (becoming visible) and BFCache return. It
   * must NOT pause: a tab becoming visible is not a reason to stop playback.
   * BFCache entry is what pauses, via pagehide(persisted) -> pause().
   */
  recoverContext(): void {
    if (this.statusValue === "disposed" || this.statusValue === "idle") return;
    if (this.tone.contextState() === "running") return;
    void this.tone.resumeContext().catch((error: unknown) => {
      this.errorLog.record("recoverContext", error);
    });
  }

  // -------------------------------------------------------------------------
  // dispose
  // -------------------------------------------------------------------------

  /** The only place permanent transport callbacks are cleared. */
  dispose(): void {
    if (this.statusValue === "disposed") return;
    try {
      this.transportController.clearAll();
      this.tone.transport().pause();
    } catch (error) {
      this.errorLog.record("dispose:transport", error);
    }
    this.queue.discardAll();
    this.click?.releaseAll();
    this.drums?.releaseAll();
    this.parameters?.stop();
    this.parameters = null;
    this.click = null;
    this.master = null;
    this.dispatcher = null;
    this.disposeRegistry.disposeAll((error) => {
      this.errorLog.record("dispose:node", error);
    });
    this.statusValue = "disposed";
  }

  // -------------------------------------------------------------------------
  // transport callbacks
  // -------------------------------------------------------------------------

  /**
   * The only reservation path. `callbackTimeSec` is Tone's callback argument and
   * is the sole time origin — Tone.now(), AudioContext.currentTime and
   * Transport.ticks are deliberately not mixed in here (9.13.1).
   */
  private onDispatchTick(callbackTimeSec: number): void {
    // INV-P2: nothing is reserved unless the engine is running. See onBarTick for
    // why this callback can arrive after pause() has already returned.
    if (this.statusValue !== "running") return;
    const dispatcher = this.dispatcher;
    if (dispatcher === null) return;
    try {
      const transport = this.tone.transport();
      const perTick = secondsPerTick(transport.bpm, transport.ppq);
      const result = dispatcher.dispatch({
        callbackTimeSec,
        callbackTicks: transport.getTicksAtTime(callbackTimeSec),
        secondsPerTick: perTick,
      });
      this.dispatchedEventTotal += result.dispatched;
      this.lateDropCount += result.lateDropped;
      this.trackDispatchRate();
    } catch (error) {
      this.errorLog.record("dispatchTick", error);
    }
  }

  /**
   * INV-P1: no BarPlan is produced unless the engine is running.
   *
   * Tone's Transport.pause() takes effect at now() = currentTime + lookAhead
   * (Clock.pause -> toSeconds(undefined) -> now(); Clock._loop invokes tick
   * callbacks up to now()). This callback can therefore still arrive for up to
   * lookAhead AFTER pause() returned and emptied the queue. Without this guard a
   * bar gets planned into the just-discarded queue, and the following dispatch
   * tick would reserve it — i.e. audio after Stop.
   */
  private onBarTick(): void {
    if (this.statusValue !== "running") return;
    try {
      this.bar += 1;
      this.beat = 0;
      this.applyBpmStep();
      this.planFrom(this.bar);
    } catch (error) {
      this.errorLog.record("barTick", error);
    }
  }

  /**
   * Deliberately NOT guarded by status: UI state must keep updating while paused.
   * It only moves a display counter and never plans or reserves anything.
   */
  private onUiTick(): void {
    this.beat = (this.beat + 1) % BEATS_PER_BAR;
    // One-shot percussion has no real note-off, so the rough active count is
    // decayed here rather than pinned at its maximum.
    this.drums?.decay();
  }

  /**
   * BPM only changes at bar boundaries and by at most BPM_MAX_STEP_PER_BAR, so
   * the <=200ms of already-reserved audio never audibly jumps.
   * Auto tempo is off by default and, when on, is derived from genre only —
   * never from energy or input level (ch.5).
   */
  private applyBpmStep(): void {
    const transport = this.tone.transport();
    const target = this.store.getSnapshot().transport.bpmManual;
    const current = transport.bpm;
    if (current === target) return;
    transport.setBpm(stepTowards(current, target, BPM_MAX_STEP_PER_BAR));
    this.assertWindowInvariant();
  }

  /** Keeps PLAN_AHEAD_BARS bars planned, without ever planning a bar twice. */
  private planFrom(currentBar: number): void {
    const controls = this.store.getSnapshot();
    const target = currentBar + PLAN_AHEAD_BARS - 1;
    for (let bar = Math.max(currentBar, this.plannedUpToBar + 1); bar <= target; bar += 1) {
      const result = planBar(
        this.musicState,
        this.streams,
        {
          genre: controls.genre,
          energy: controls.main.energy,
          density: controls.main.density,
        },
        {
          ticksPerBar: this.ticksPerBar,
          idBase: this.nextEventId,
          clickEnabled: controls.diagnosticClick,
        },
      );
      this.nextEventId += result.idsUsed;
      this.queue.push(result.events);
      this.plannedUpToBar = bar;
    }
  }

  private createSink(): DispatchSink {
    return {
      note: (event: NoteEvent, timeSec: number) => {
        const durationSec = Math.max(0.01, event.durationTicks * this.currentSecondsPerTick());
        if (event.instrument === "diagnosticClick") {
          this.click?.trigger(event.midi, durationSec, timeSec, event.velocity);
          return;
        }
        this.drums?.trigger(event.instrument, event.midi, durationSec, timeSec, event.velocity);
      },
      ramp: (event: RampEvent, timeSec: number) => {
        // Phase 1A plans contain no ramp events; kept so the type stays honest.
        this.errorLog.record(
          "dispatch:ramp",
          new Error(`unhandled ramp param ${event.param} at ${timeSec}`),
        );
      },
    };
  }

  // -------------------------------------------------------------------------
  // derived values
  // -------------------------------------------------------------------------

  /** Current music inputs, read fresh from the store. */
  private musicStateInputs() {
    const controls = this.store.getSnapshot();
    return {
      genre: controls.genre,
      energy: controls.main.energy,
      density: controls.main.density,
    };
  }

  private get ticksPerBar(): number {
    return this.tone.transport().ppq * BEATS_PER_BAR;
  }

  private currentSecondsPerTick(): number {
    const transport = this.tone.transport();
    return secondsPerTick(transport.bpm, transport.ppq);
  }

  private nextResumeBar(): number {
    const ticks = this.tone.transport().ticks;
    return Math.ceil(ticks / this.ticksPerBar);
  }

  /** Time that must pass before pre-pause reservations can no longer sound. */
  private reservationDrainSec(): number {
    return DISPATCH_WINDOW_SEC + this.tone.lookAheadSec() + RESERVATION_SAFETY_SEC;
  }

  private reservationDrainRemainingMs(): number {
    const readyAt = this.pausedAtSec + this.reservationDrainSec();
    return Math.max(0, (readyAt - this.tone.nowSec()) * 1000);
  }

  /** INV-W1: the fixed window must comfortably exceed one dispatch tick period. */
  private assertWindowInvariant(): void {
    const transport = this.tone.transport();
    const tickPeriod = secondsPerTick(transport.bpm, transport.ppq) * (transport.ppq / 8);
    if (tickPeriod <= 0) return;
    if (DISPATCH_WINDOW_SEC < tickPeriod * MIN_WINDOW_TO_TICK_RATIO) {
      if (!this.windowWarningIssued) {
        this.windowWarningIssued = true;
        this.errorLog.record(
          "invariant:dispatchWindow",
          new Error(
            `dispatch window ${DISPATCH_WINDOW_SEC}s is too small for tick period ${tickPeriod.toFixed(3)}s at ${transport.bpm} BPM`,
          ),
        );
      }
    }
  }

  private trackDispatchRate(): void {
    const nowMs = this.tone.nowSec() * 1000;
    this.dispatchTickCount += 1;
    if (this.dispatchRateWindowStartMs === 0) {
      this.dispatchRateWindowStartMs = nowMs;
      return;
    }
    const elapsed = nowMs - this.dispatchRateWindowStartMs;
    if (elapsed >= 1000) {
      this.dispatchTickHz = (this.dispatchTickCount * 1000) / elapsed;
      this.dispatchTickCount = 0;
      this.dispatchRateWindowStartMs = nowMs;
    }
  }

  // -------------------------------------------------------------------------
  // snapshot
  // -------------------------------------------------------------------------

  readSnapshot(uiFps: number): EngineSnapshot {
    const controls = this.store.getSnapshot();
    const transport = this.tone.transport();
    const alive = this.statusValue !== "idle" && this.statusValue !== "disposed";

    return {
      status: this.statusValue,
      isResuming: this.resumePending,
      bpm: alive ? Math.round(transport.bpm) : controls.transport.bpmManual,
      autoTempo: controls.transport.autoTempo,
      bar: this.bar,
      beat: this.beat,
      resumeBar: alive ? this.nextResumeBar() : 0,
      seed: controls.seed,
      diagnostics: {
        audioContextState: this.tone.contextState(),
        baseLatency: this.tone.baseLatencySec(),
        outputLatency: this.tone.outputLatencySec(),
        lookAhead: this.tone.lookAheadSec(),
        transportCallbackCount: this.eventRegistry.size,
        plannedBarCount: Math.max(0, this.plannedUpToBar - this.bar + 1),
        pendingDispatchEventCount: this.queue.pendingCount,
        activeVoiceCount: (this.click?.activeVoiceCount ?? 0) + (this.drums?.activeVoiceCount ?? 0),
        nodeCountEstimate: this.disposeRegistry.size,
        dispatchedEventTotal: this.dispatchedEventTotal,
        lateDropCount: this.lateDropCount,
        masterAppliedGain: this.master?.gain ?? 0,
        masterMuteReason: this.master?.reason ?? "starting",
        resumeDrainRemainingMs: this.resumePending ? this.reservationDrainRemainingMs() : 0,
        uiFps,
        dispatchTickHz: this.dispatchTickHz,
        lastError: this.errorLog.lastText,

        genreNormalized: this.musicState.genre,
        currentEnergy: this.musicState.energy.current,
        currentDensity: this.musicState.density.current,
        currentSection: this.musicState.section.name,
        rhythmPatternHash: this.musicState.lastBarReport.patternHash,
        activeRhythmRoles: this.musicState.lastBarReport.activeRoles,
        eventsThisBar: this.musicState.lastBarReport.eventCount,
        averageVelocity: this.musicState.lastBarReport.averageVelocity,
        averageTimingOffsetMs: this.musicState.lastBarReport.averageTimingOffsetMs,
      },
    };
  }
}
